import React, { useState } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { specialtyStructure } from '../../data/specialtyStructure';
import { PLANNER_TYPES, plannerKey, plannerError } from './plannerTypes';

const LABELS = { hide: 'Ocultar del planificador', show: 'Mostrar en el planificador', move: 'Mover archivos', delete: 'Eliminar de la app' };
const DETAILS = {
  hide: 'El contenido seguirá disponible en la app. Se excluirá de planes nuevos o recalculados; los planes guardados no se modificarán.',
  show: 'El contenido volverá a incluirse en planes nuevos o recalculados. Las Masterclasses deben estar publicadas.',
  move: 'Se cambiará su módulo y submódulo en la app, conservando contenido, enlaces y progreso. Los planes guardados conservarán su distribución hasta recalcularlos.',
  delete: 'Se retirarán estos archivos y sus preguntas de la app. Los enlaces dejarán de funcionar. Se conservará el historial de resultados. Esta acción no se puede deshacer desde el panel.',
};

export const PlannerBulkDialog = ({ request, apiClient, onClose, onApplied }) => {
  const [module, setModule] = useState(request.module || '');
  const [submodule, setSubmodule] = useState(request.submodule || '');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const config = specialtyStructure[module];
  const valid = request.action !== 'move' || (module && (!config?.hasSubmodules || submodule));
  const review = async () => {
    setBusy(true); setError('');
    try {
      const response = await apiClient.post('/admin/planner-content/preview', {
        action: request.action, items: request.items.map(({ id, type }) => ({ id, type })), module, submodule,
      });
      setPreview(response.data);
    } catch (e) { setError(plannerError(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setError('');
    try {
      const response = await apiClient.post('/admin/planner-content/apply', { preview_id: preview.preview_id, confirmed: true });
      await onApplied(response.data);
    } catch (e) { setError(plannerError(e)); }
    finally { setBusy(false); }
  };
  return <AlertDialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <AlertDialogContent data-testid="planner-bulk-dialog" className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto rounded-lg p-4 sm:p-6">
      <AlertDialogHeader>
        <AlertDialogTitle data-testid="planner-bulk-title">{LABELS[request.action]}</AlertDialogTitle>
        <AlertDialogDescription data-testid="planner-bulk-description">{DETAILS[request.action]}</AlertDialogDescription>
      </AlertDialogHeader>
      {request.action === 'move' && !preview && <div className="grid gap-3">
        <label className="text-sm">Módulo
          <select data-testid="planner-move-module" value={module} disabled={busy} onChange={e => { setModule(e.target.value); setSubmodule(''); }} className="mt-1 w-full min-w-0 border rounded-md p-2 bg-white">
            <option value="">Selecciona un módulo</option>
            {Object.keys(specialtyStructure).map(name => <option key={name} value={name}>{name}</option>)}
            {request.items.every(item => item.type === 'simulacro') && <option value="Simulacros">Simulacros</option>}
          </select>
        </label>
        {config?.hasSubmodules && <label className="text-sm">Submódulo
          <select data-testid="planner-move-submodule" value={submodule} disabled={busy} onChange={e => setSubmodule(e.target.value)} className="mt-1 w-full min-w-0 border rounded-md p-2 bg-white">
            <option value="">Selecciona un submódulo</option>
            {config.submodules.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>}
      </div>}
      {preview?.action === 'move' && <p data-testid="planner-move-destination" className="text-sm font-semibold break-words">Destino: {preview.module} · {preview.submodule}</p>}
      <p data-testid="planner-bulk-count" className="text-sm font-semibold">{request.items.length} archivo(s)</p>
      <ul data-testid="planner-bulk-items" className="max-h-44 overflow-y-auto space-y-2 text-sm">
        {(preview?.items || request.items).map(item => <li data-testid={`planner-bulk-item-${plannerKey(item)}`} className="break-words [overflow-wrap:anywhere]" key={plannerKey(item)}>
          {item.title} <span className="text-gray-500">· {PLANNER_TYPES[item.type]?.label}{item.question_count != null ? ` · ${item.question_count} reactivos` : ''}</span>
        </li>)}
      </ul>
      {preview && request.action === 'delete' && <label className="text-sm">Escribe ELIMINAR para confirmar
        <Input data-testid="planner-delete-confirmation" value={confirmation} disabled={busy} onChange={e => setConfirmation(e.target.value)} autoComplete="off" className="mt-1" />
      </label>}
      {error && <p role="alert" data-testid="planner-bulk-error" className="text-sm text-red-700 break-words">{error}</p>}
      <AlertDialogFooter className="gap-2">
        <Button data-testid="planner-bulk-cancel" variant="outline" disabled={busy} onClick={onClose}>Cancelar</Button>
        {!preview ? <Button data-testid="planner-bulk-review" disabled={!valid || busy} onClick={review}>{busy ? 'Revisando…' : 'Revisar cambios'}</Button>
          : <Button data-testid="planner-bulk-confirm" variant={request.action === 'delete' ? 'destructive' : 'default'} disabled={busy || (request.action === 'delete' && confirmation !== 'ELIMINAR')} onClick={apply}>{busy ? 'Aplicando…' : 'Confirmar'}</Button>}
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
};